import React, { useEffect, useState } from 'react';
import { Range, getTrackBackground } from 'react-range';
import Select, { MultiValue } from 'react-select';
import { toast } from '../../../../../ui/toast';
import { Button, Drawer } from '../../../../../ui';
import { MultiSelectListItem } from '../../../../../ui/data_source_filter/MultiSelectListItem';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import { saveFilter } from '../../../../../utils/api_service/JobOpenings';
import { JobApplicantCSV, JobApplicantSearchRequest, Skill } from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { TopicInput } from '../../../../questions/upload_file/topic_input';
import SkillWeighting from '../UI/SkillWeighting';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { ViewInArOutlined } from '@mui/icons-material';

type AnalyticFilterProps = {
    isOpen: boolean,
    onCloseDrawer: () => void,
    setStatus: (status: string[]) => void | null,
    statusValue: string[],
    minMaxAge: number[],
    setMinMaxAge: (minMax: number[]) => void,
    behavioralAttributes: string,
    setBehavioralAttributes: React.Dispatch<React.SetStateAction<string>>,
    defaultJobTitles: string,
    setDefaultJobTitles: React.Dispatch<React.SetStateAction<string>>,
    setSimilarKeyword: React.Dispatch<React.SetStateAction<string>>,
    similarKeyword: string,
    loading: boolean,
    genderList: string[],
    setGenderList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    statusList: string[],
    setStatusList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    similarToggle: boolean,
    setSimilarToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    ageToggle: boolean,
    setAgeToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    searchObject: JobApplicantSearchRequest,
    setSearchObject: (
        jobApplicantSearchRequest: JobApplicantSearchRequest
    ) => void,
    searchCSVObject: JobApplicantCSV,
    setSearchCSVObject: (JobApplicantCSV: JobApplicantCSV) => void,
    keywordList: string[],
    setKeywordList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    jobOpId: string,
    extractedSkill: Skill[],
    setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedJobTitles: Skill[],
    setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedKeywords: Skill[],
    setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedBehavioralScore: Skill[],
    setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    weightingEnabled: boolean,
    setWeightingEnabled: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordWeightage: boolean,
    setKeywordWeightage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    similarBehavior: boolean,
    setSimilarBehavior: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordToggle: boolean,
    setKeywordToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    similarJobTitle: boolean,
    setSimilarJobTitle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
};

export const AnalyticFilter = ({
  isOpen,
  onCloseDrawer,
  setStatus,
  statusValue,
  minMaxAge,
  setMinMaxAge,
  defaultJobTitles,
  setDefaultJobTitles,
  behavioralAttributes,
  setBehavioralAttributes,
  setSimilarKeyword,
  similarKeyword,
  loading,
  genderList,
  setGenderList,
  statusList,
  setStatusList,
  similarToggle,
  setSimilarToggle,
  ageToggle,
  setAgeToggle,
  searchObject,
  setSearchObject,
  searchCSVObject,
  setSearchCSVObject,
  keywordList,
  jobOpId,
  extractedSkill,
  setExtractedSkill,
  extractedJobTitles,
  setExtractedJobTitles,
  extractedKeywords,
  setExtractedKeywords,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
  weightingEnabled,
  setWeightingEnabled,
  keywordWeightage,
  setKeywordWeightage,
  similarBehavior,
  setSimilarBehavior,
  keywordToggle,
  setKeywordToggle,
  similarJobTitle,
  setSimilarJobTitle,
  setUseSkillPercentage,
}: AnalyticFilterProps) => {

  const [genderInput, setGenderInput] = useState<SelectOptions[]>([]);
  const [statusInput, setStatusInput] = useState<SelectOptions[]>(
    statusList.map((status) =>
      status === 'Unsuccessful-Pending'
        ? { value: status, label: 'Unsuccessful' }
        : { value: status, label: status },
    ),
  );

  useEffect(() => {
    setGenderInput(
      genderList.map((gender) => ({ value: gender, label: gender })),
    );
  }, [genderList]);

  const convertStringToObjects = (
    input: string,
  ): { label: string; value: string }[] => {
    if (input === '') {
      return [];
    } else {
      const value = input.split(':').map((item) => ({
        label: item,
        value: item,
      }));
      return value;
    }
  };

  const genderItems = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ];

  const statusItems = [
    { value: 'New', label: 'New' },
    { value: 'Suitable', label: 'Suitable' },
    { value: 'Auto-Assessment', label: 'Auto Assessment' },
    { value: 'Outstanding', label: 'Outstanding' },
    { value: 'Shortlisted', label: 'ShortListed' },
    { value: 'Unsuccessful-Pending', label: 'Unsuccessful' },
  ];

  const handleMultiSelectChange = (
    selectedValues: MultiValue<SelectOptions>,
  ) => {
    const selected = selectedValues.map((option) => option.value);
    setGenderList(selected);
    setGenderInput(selectedValues as SelectOptions[]);
  };

  const handleApplicationStatus = (
    selectedValues: MultiValue<SelectOptions>,
  ) => {
    const selected = selectedValues.map((option) => option.value);
    setStatusList(selected);
    const selectedStatus = selectedValues.map((option) => option.label);
    setStatus(selectedStatus);
    setStatusInput(selectedValues as SelectOptions[]);
  };

  const handleApplyChange = async () => {

    const jobTitles = extractedJobTitles.map(item => item.skill);
    const behavioralScore = extractedBehavioralScore.map(item => item.skill);
    const keywords = extractedKeywords.map(({ skill, weight }) => ({
      keyword: skill,
      weight: weight,
    }));
    const cleanedSkills = extractedSkill.map(({ skill, weight }) => ({
      skill,
      weight,
    }));

    setUseSkillPercentage(weightingEnabled);

    const data = {
      ...searchObject,
      age: {
        ...searchObject.age,
        min_age: minMaxAge[0],
        max_age: minMaxAge[1],
        include_unknown: ageToggle,
      },
      additional_skills: {
        skill_weightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      job_title: {
        similar: similarJobTitle,
        job_titles: jobTitles.length === 0 ? [] : jobTitles,
      },
      keywords: {
        keyword_weightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
      behavioral_attributes: {
        similar: similarBehavior,
        behavioral_attributes:
          behavioralScore.length === 0 ? [] : behavioralScore,
      },
      gender: genderList,
      applicant_status: statusList,
    };

    try {
      await saveFilter(jobOpId, data);
    } catch (error) {
      toast.error('Error Loading Job Applicants', {
        position: 'bottom-center',
      });
    }

    setSearchObject(data);

    setSearchCSVObject({
      ...searchCSVObject,
      age: {
        ...searchCSVObject.age,
        min_age: minMaxAge[0],
        max_age: minMaxAge[1],
        include_unknown: ageToggle,
      },
      additional_skills: {
        skill_weightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      behavioral_attributes: {
        similar: similarBehavior,
        behavioral_attributes:
          behavioralScore.length === 0 ? [] : behavioralScore,
      },
      job_title: {
        similar: similarJobTitle,
        job_titles: jobTitles.length === 0 ? [] : jobTitles,
      },
      gender: genderList,
      applicant_status: statusList,
      keywords: {
        keyword_weightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ width: '100%' }}>
          <Button label="Apply" onClick={() => handleApplyChange()} variant="primary"/>
          <Button label="Close" onClick={onCloseDrawer} variant="secondary"/>
        </Stack>
      }
      title="Apply Filters"
      width="w-full md:w-[740px] lg:w-[800px]"
    >
      <Stack spacing={2}>
        {/* Section: Applicant Attributes */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '6px',
                bgcolor: '#ecfdf5',
                color: '#15803d',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              📊
            </Box>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1f2937' }}>
                Graph Axis Filters
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                Configure 3D axes, importance weighting, and term matching
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 1.75,
              alignItems: 'stretch',
            }}
          >
            {/* Skill(s) */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...(loading && { animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }) }}>
              <SkillWeighting
                title={'X: Skill(s)'}
                attributeName={'Skill'}
                isWeightRequired={true}
                extractedSkill={extractedSkill}
                setExtractedSkill={setExtractedSkill}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarToggle}
                setSimilarEnabled={setSimilarToggle}
              />
            </Box>
            {/* Experience Job Titles */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...(loading && { animation: 'pulse 2s infinite' }) }}>
              <SkillWeighting
                title={'Y: Experience Job Title(s)'}
                attributeName={'job title'}
                extractedSkill={extractedJobTitles}
                setExtractedSkill={setExtractedJobTitles}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarJobTitle}
                setSimilarEnabled={setSimilarJobTitle}
              />
            </Box>

            {/* Separator */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                my: 0.5,
              }}
            >
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, #e5e7eb, transparent)' }} />
              <Chip
                size="small"
                icon={<ViewInArOutlined sx={{ fontSize: 12, color: '#059669' }} />}
                label="3D Graph Extended Axes"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: '#6b7280',
                  bgcolor: '#f9fafb',
                  border: '1px solid',
                  borderColor: 'rgba(229,231,235,0.8)',
                  boxShadow: '0 0 1px rgba(0,0,0,0.05)',
                }}
              />
              <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, #e5e7eb, transparent)' }} />
            </Box>

            {/* Behavioral Attributes */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...(loading && { animation: 'pulse 2s infinite' }) }}>
              <SkillWeighting
                title={'Z: Behavioral Attribute(s)'}
                attributeName={'attribute'}
                extractedSkill={extractedBehavioralScore}
                setExtractedSkill={setExtractedBehavioralScore}
                weightingEnabled={weightingEnabled}
                setWeightingEnabled={setWeightingEnabled}
                similarEnabled={similarBehavior}
                setSimilarEnabled={setSimilarBehavior}
              />
            </Box>

            {/* Keywords Counter */}
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...(loading && { animation: 'pulse 2s infinite' }) }}>
              <SkillWeighting
                title={'Keyword Counter'}
                attributeName={'keyword'}
                isWeightRequired={true}
                extractedSkill={extractedKeywords}
                setExtractedSkill={setExtractedKeywords}
                weightingEnabled={keywordWeightage}
                setWeightingEnabled={setKeywordWeightage}
                similarEnabled={keywordToggle}
                setSimilarEnabled={setKeywordToggle}
              />
            </Box>
          </Box>
        </Paper>

        {/* Section: Filters */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'grey.200',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '6px',
                bgcolor: '#eff6ff',
                color: '#1d4ed8',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              👤
            </Box>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1f2937' }}>
                Candidate Attributes Filters
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                Filter applicants by status, gender, and age demographics
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 1.75,
            }}
          >
            {/* Applicant Status */}
            <Box sx={loading ? { animation: 'pulse 2s infinite' } : {}}>
              <Typography component="label" sx={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.75 }}>
                Applicant Status
              </Typography>
              {loading ? (
                <Box sx={{ height: 36, width: '100%', borderRadius: '6px', bgcolor: '#e5e7eb' }} />
              ) : (
                statusValue.length === 0 && (
                  <Select
                    placeholder="Select Application Status"
                    name="assessmentType"
                    options={statusItems}
                    onChange={handleApplicationStatus}
                    isClearable
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: MultiSelectListItem }}
                    value={statusInput}
                    isMulti
                    styles={{
                      control: (base: any) => ({ ...base, fontSize: '0.75rem' }),
                      menu: (base: any) => ({ ...base, fontSize: '0.75rem' }),
                    }}
                  />
                )
              )}
            </Box>

            {/* Gender */}
            <Box sx={loading ? { animation: 'pulse 2s infinite' } : {}}>
              <Typography component="label" sx={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', mb: 0.75 }}>
                Gender
              </Typography>
              {loading ? (
                <Box sx={{ height: 36, width: '100%', borderRadius: '6px', bgcolor: '#e5e7eb' }} />
              ) : (
                <Select
                  placeholder="Select Gender"
                  name="gender"
                  options={genderItems}
                  onChange={handleMultiSelectChange}
                  isClearable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={{ Option: MultiSelectListItem }}
                  value={genderInput}
                  isMulti
                  styles={{
                    control: (base: any) => ({ ...base, fontSize: '0.75rem' }),
                    menu: (base: any) => ({ ...base, fontSize: '0.75rem' }),
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Age Group */}
          <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography component="label" sx={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                Age Group
              </Typography>
              <Chip
                size="small"
                label={`${minMaxAge[0]} – ${minMaxAge[1]} yrs`}
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#15803d',
                  bgcolor: '#ecfdf5',
                  border: '1px solid',
                  borderColor: 'rgba(167,243,208,0.7)',
                }}
              />
            </Stack>
            <Box sx={{ px: 0.5, py: 1 }}>
              <Range
                values={minMaxAge}
                step={1}
                min={0}
                max={100}
                onChange={(newValues: number[]) => setMinMaxAge(newValues)}
                renderTrack={({ props, children }) => (
                  <Box
                    {...props}
                    sx={{ height: 6, width: '100%', borderRadius: '3px' }}
                    style={{
                      ...props.style,
                      background: getTrackBackground({
                        values: minMaxAge,
                        colors: ['#E5E7EB', '#059669', '#E5E7EB'],
                        min: 0,
                        max: 100,
                      }),
                      alignSelf: 'center',
                    }}
                  >
                    {children}
                  </Box>
                )}
                renderThumb={({ props, isDragged }) => (
                  <Box
                    {...props}
                    style={{
                      ...props.style,
                      height: '18px',
                      width: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#FFF',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      border: isDragged ? '2px solid #059669' : '1px solid #D1D5DB',
                    }}
                  >
                    <Box
                      sx={{
                        height: 8,
                        width: 2,
                        borderRadius: '1px',
                        bgcolor: isDragged ? '#059669' : '#9ca3af',
                      }}
                    />
                  </Box>
                )}
              />
            </Box>
          </Box>

          {/* Toggles */}
          <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <ToggleInput
              toggled={ageToggle}
              handleToggle={() => setAgeToggle(!ageToggle)}
              name="edit"
              informationText="Include Unknown Ages"
            />
          </Box>
        </Paper>
      </Stack>
    </Drawer>
  );
};
